// src/lib/templateRender.js
// Variable-substitution engine for document_templates.
//
// Templates are plain text with {{path.to.value}} placeholders. The
// `renderTemplate(body, context)` helper walks every placeholder and
// substitutes the value from `context` (dot-notation resolves nested
// objects). Missing values render as an empty string by default — we
// could also render `{{path}}` literally as a debugging aid, but for
// finished-document output, blank is almost always what the user wants.
//
// Standard context shape (built by the calling component):
//   {
//     firm:    { name, address, phone, email, ... },
//     matter:  { title, case_number, opened_at, status, ... },
//     client:  { full_name, email, phone, address, ... },
//     user:    { full_name, email },           // the current signed-in user
//     today:   '2026-05-24',
//     now:     '2026-05-24 14:32',
//   }
//
// Why a custom mini-language instead of Mustache / Handlebars:
//   * We control the surface area exactly — no helpers, no conditionals,
//     no loops — so the security audit is one screen.
//   * No dependency added. The whole engine is ~30 lines.
//   * If we ever need conditionals ({{#if}}) or loops, we can swap to
//     a real templating library at that point.

// The {{...}} placeholder regex. `[^}]+?` inside is lazy so adjacent
// placeholders don't merge. Whitespace around the path is allowed.
const PLACEHOLDER_RE = /\{\{\s*([^}]+?)\s*\}\}/g;

/**
 * Resolves a dot-path against an object. Returns undefined for any
 * missing segment (no throws).
 *
 *   getPath({ a: { b: 'x' } }, 'a.b')   // 'x'
 *   getPath({ a: { b: 'x' } }, 'a.c')   // undefined
 *   getPath(null, 'a.b')                // undefined
 */
export function getPath(obj, path) {
  if (!obj || typeof obj !== 'object') return undefined;
  const segs = String(path || '').split('.');
  let cur = obj;
  for (const s of segs) {
    if (cur == null) return undefined;
    cur = cur[s];
  }
  return cur;
}

/**
 * Renders a template body by replacing every {{path.to.value}} with
 * the corresponding value from `context`.
 *
 * - Missing values render as empty string (override with `options.onMissing`)
 * - Non-string values get String()-coerced (null → empty, dates →
 *   ISO string by default — preformat in context if you want
 *   something fancier)
 * - Returns the original body unchanged if no placeholders exist
 */
export function renderTemplate(body, context, options = {}) {
  const onMissing = options.onMissing || (() => '');
  if (typeof body !== 'string' || body.length === 0) return '';
  return body.replace(PLACEHOLDER_RE, (_match, rawPath) => {
    const path  = rawPath.trim();
    const value = getPath(context, path);
    if (value === undefined || value === null) return onMissing(path);
    if (value instanceof Date)                return value.toISOString().slice(0, 10);
    return String(value);
  });
}

/**
 * Scans a template body and returns the set of unique placeholder paths
 * it references. Used by the editor UI to show "this template uses: X, Y, Z"
 * and to validate that merge_fields stays in sync with actual usage.
 *
 *   extractPlaceholders('Hi {{client.name}}, re: {{matter.title}} on {{today}}.')
 *   // → ['client.name', 'matter.title', 'today']
 */
export function extractPlaceholders(body) {
  if (typeof body !== 'string' || body.length === 0) return [];
  const found = new Set();
  let m;
  // Reset the regex's lastIndex since it has /g flag (stateful).
  PLACEHOLDER_RE.lastIndex = 0;
  while ((m = PLACEHOLDER_RE.exec(body)) !== null) {
    found.add(m[1].trim());
  }
  return Array.from(found);
}

/**
 * Convenience: builds a sensible context object from the typical
 * (firm, matter, client, user) tuple. Adds `today` and `now` so
 * templates can use them without the caller having to remember.
 *
 * Any of (firm, matter, client, user) may be null — paths through
 * them resolve to undefined which renders as empty string.
 */
export function buildContext({ firm, matter, client, user } = {}) {
  const now    = new Date();
  const isoDay = now.toISOString().slice(0, 10);
  const isoMin = now.toISOString().slice(0, 16).replace('T', ' ');
  return {
    firm:   firm   || {},
    matter: matter || {},
    client: client || {},
    user:   user   || {},
    today:  isoDay,
    now:    isoMin,
  };
}
